import * as React from 'react';

import { Slot } from '@radix-ui/react-slot';
import { tv, type VariantProps } from 'tailwind-variants';

import cn from '../../utils/cn';

const typographyVariants = tv({
  base: 'break-words text-gray-black',
  variants: {
    variant: {
      display: 'text-lg font-bold leading-120',
      body: 'text-sm font-normal leading-135',
      'heading-1': 'text-xl font-medium leading-128',
      'heading-2': 'text-md font-bold leading-135',
      'description-title': 'text-sm font-medium leading-135',
      'description-list-medium': 'text-sm font-medium leading-135',
      'description-list-default': 'text-sm font-normal leading-135',
      'button-label': 'text-sm font-bold leading-120',
      'menu-default': 'text-sm font-normal leading-100',
      'menu-active': 'text-sm font-bold leading-100',
      'input-placeholder': 'text-sm font-light leading-135',
      'input-label': 'text-sm font-medium leading-135',
      'input-text': 'text-sm font-normal leading-135',
      'calendar-default': 'text-sm font-normal leading-100',
      'calendar-selected': 'text-sm font-bold leading-100',
      'caption-bold': 'text-xs font-bold leading-142',
      'caption-medium': 'text-xs font-medium leading-142',
      'caption-default': 'text-xs font-normal leading-142',
      'chip-label': 'text-xs font-medium leading-142',
    },
    color: {
      primary: 'text-gray-black',
      secondary: 'text-gray-dark',
      danger: 'text-danger',
      white: 'text-white',
    },
  },
  defaultVariants: {
    color: 'primary',
    variant: 'body',
  },
});

export interface TypographyProps
  extends React.ComponentPropsWithoutRef<'p'>,
    VariantProps<typeof typographyVariants> {
  asChild?: boolean;
  color?: 'primary' | 'secondary' | 'white' | 'danger';
}

const Typography = React.forwardRef<HTMLDivElement, TypographyProps>(
  ({ className, variant, color, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'p';
    return (
      <Comp
        className={cn(typographyVariants({ variant, className, color }))}
        ref={ref}
        {...props}
      />
    );
  }
);

Typography.displayName = 'Typography';

export default Typography;
